import { Head, Link } from "@inertiajs/react"
import { ChevronRight, User as UserIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type UserRow = {
  id: number
  name: string
  email: string
  roles: string[]
  ad_free_lifetime: boolean
  ad_free_until: string | null
  donations_count: number
  created_at: string
  email_verified: boolean
  email_verified_at: string | null
  contributions_count: number
}

// Unverified alone is not suspicious — contributors imported from the old app
// never received a verification email. A suspect has never verified, never
// contributed, and holds no role (#363).
function isSuspect(user: UserRow): boolean {
  return !user.email_verified && user.contributions_count === 0 && user.roles.length === 0
}

function adFreeLabel(user: UserRow): string | null {
  if (user.ad_free_lifetime) return "Ad-free forever"
  if (user.ad_free_until && new Date(user.ad_free_until) > new Date()) {
    return `Ad-free until ${formatDate(user.ad_free_until)}`
  }
  return null
}

export default function AdminUsersIndex({ users, unverified_count, suspect_count }: { users: UserRow[]; unverified_count: number; suspect_count: number }) {
  return (
    <>
      <Head title="Users">
        <meta name="description" content="Manage users in the admin area." />
        <meta property="og:title" content="Users" />
        <meta property="og:description" content="Manage users in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Users</h1>
              <p className="mt-1">
                {users.length} {users.length === 1 ? "user" : "users"} in this app.
                {unverified_count > 0 && (
                  <>
                    {" "}
                    {unverified_count} {unverified_count === 1 ? "account has" : "accounts have"} never verified their email
                    {suspect_count > 0 ? (
                      <>
                        {", of which "}
                        <span className="text-danger-display font-medium">
                          {suspect_count} {suspect_count === 1 ? "has" : "have"} no contributions and no role
                        </span>
                        {" — those are the ones worth a look."}
                      </>
                    ) : (
                      " — all of them have contributions or a role, so they are most likely imported accounts that never got the email."
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
          {users.map((user) => {
            const adFree = adFreeLabel(user)
            return (
              <li key={user.id}>
                <Link
                  href={`/admin/users/${user.id}`}
                  className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                >
                  <UserIcon className="h-4 w-4 text-ink-muted shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink-display">
                      {user.name || user.email}
                    </div>
                    <div className="truncate text-xs text-ink-muted">
                      {user.email} · Joined {formatDate(user.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!user.email_verified && (
                      <Badge tone={isSuspect(user) ? "danger" : "muted"}>Unverified</Badge>
                    )}
                    {adFree && <Badge tone="signal">{adFree}</Badge>}
                    {user.roles.includes("admin") && <Badge tone="accent">Admin</Badge>}
                    {user.roles.includes("editor") && <Badge tone="neutral">Editor</Badge>}
                    {user.roles.includes("contributor") && <Badge tone="neutral">Contributor</Badge>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
                </Link>
              </li>
            )
          })}
        </ul>
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
